import { useInterpret } from '@xstate/react';
import { isEqual } from 'lodash';
import * as React from 'react';
import { InterpreterFrom, assign } from 'xstate';

import { checkCategoryLost, checkChoiceLost } from '~src/category/check-category-lost';
import { bootstrapConfig } from '~src/context/config';
import { useGlobalState } from '~src/global-state';
import { useIpc } from '~src/utils/ipc-utils';

import { isAddressEmpty, useHasAddress } from '~address-form/utils/address-utils';

import { useDeliveryAddressMutation } from '../delivery-address-mutation';
import {
  RecipientFlowMachine,
  createRecipientFlowMachine,
} from '../state-machines/recipient-flow-machine';

const RecipientFormFlowContext = React.createContext<
  | {
      recipientFlowService: InterpreterFrom<RecipientFlowMachine>;
      editInitialAddress: boolean;
      initialAddressEntry: boolean;
    }
  | undefined
>(undefined);

export const RecipientFormFlowProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const ipc = useIpc();
  const recipientMutation = useDeliveryAddressMutation();
  const globalState = useGlobalState();
  const hasAddress = useHasAddress();

  const initialRecipientEntry = !globalState.recipient || isAddressEmpty(globalState.recipient);
  const editInitialRecipient = !!globalState.recipient && !bootstrapConfig.userId;

  const [recipientFlowMachine] = React.useState(() =>
    createRecipientFlowMachine({
      initialEntry: initialRecipientEntry,
      hideAddressBook: globalState.address_form_configuration?.features?.hide_address_book,
      externalAddressBookProvider: bootstrapConfig.externalAddressBookProvider,
      addressBookLength: globalState.address_book?.addresses?.length,
    })
  );

  const recipientFlowService = useInterpret(
    recipientFlowMachine,
    {
      services: {
        updateAddressService: async (ctx, event) => {
          const res = {
            categoryLost: false,
            locationLost: false,
            addressBookLength: ctx.addressBookLength,
          };
          const onError = 'onError' in event.data ? event.data.onError : undefined; // it's ugly but that's how typescript is rollin'

          const stateResponse = await recipientMutation.mutateAsync(event.data.address, {
            onError,
          });

          if (!stateResponse || !hasAddress) {
            // do not track categories in pre-address because we do not display them.
            return res;
          }

          if (stateResponse.address_book?.addresses) {
            res.addressBookLength = stateResponse.address_book?.addresses?.length;
          }

          if (checkCategoryLost(stateResponse, globalState)) {
            res.categoryLost = true;
          } else if (checkChoiceLost(stateResponse, globalState)) {
            res.locationLost = true;
          }

          return res;
        },
      },
      actions: {
        resetCategoryLost: assign(ctx => {
          recipientMutation.reset();
          return { ...ctx, categoryLost: false };
        }),
        resetLocationLost: assign(ctx => ({ ...ctx, locationLost: false })),
      },
      guards: {
        editInitialAddress: () =>
          editInitialRecipient && !bootstrapConfig.externalAddressBookProvider, // we don't rely on user id in case of external provider
        isRecipientTheSame: (_ctx, e) => isEqual(globalState.recipient, e.data.address), // TODO probably should be deepequal
        noOptionsForAddress: ctx =>
          ctx.errorMessage === 'failed to update recipient: HTTP 400: configuration error' &&
          !!globalState.sessions[0].flags.enable_alter_no_options_modal_message,
        isInitialEntry: () => initialRecipientEntry, // guard is defined here because we can't rely on memoized argument
      },
    },
    state => {
      ipc.send({
        name: 'sync_recipient_fsm_state',
        data: {
          context: state.context,
          meta: {
            fetching: state.hasTag('fetching'),
          },
        },
      });
    }
  );

  React.useState(() => recipientFlowService.start());

  React.useEffect(() => {
    if (globalState.address_book?.addresses?.length) {
      recipientFlowService.send({
        type: 'UPDATE_COUNT',
        data: { addressBookLength: globalState.address_book.addresses.length },
      });
    }
  }, [globalState]);

  return (
    <RecipientFormFlowContext.Provider
      value={{
        recipientFlowService,
        editInitialAddress: editInitialRecipient,
        initialAddressEntry: initialRecipientEntry,
      }}
    >
      {children}
    </RecipientFormFlowContext.Provider>
  );
};

RecipientFormFlowContext.displayName = 'RecipientFormFlowProvider';

export function useRecipientFormFlow() {
  const state = React.useContext(RecipientFormFlowContext);
  if (state !== undefined) {
    return state;
  }
  throw 'RecipientFormFlowContext is not yet defined, you are probably using it in a wrong place';
}
